import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import defaults from 'lodash/fp/defaults';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';

import DeviceUpdateEventsContainer from './DeviceUpdateEventsContainer';
import DeviceUpdateEventsFilters from './DeviceUpdateEventsFilters';
import {
    ALL,
    createDeliverablesOptions,
    createDeliverablesVersionOptions,
} from '~/features/base/constants/filterOptions';
import {
    fetchFilteredUpdateEvents,
} from '~/features/updateEvents/actions/updateEventActions';
import {
    deviceUpdateEventsSelector,
} from '~/features/updateEvents/selectors/updateEventSelectors';
import { queryToSearchCriteria } from '~/features/updateEvents/utils/updateEventsQuery';
import { packageEntitiesSelector } from '~/features/deliverables/features/packages/selectors/packageSelectors';
import { filterDeliverableVersionsSelector } from '~/features/base/selectors/filterSelectors';
import { fetchPackages } from '~/features/deliverables/features/packages/actions/packageActions';
import {
    fetchPackageDeliverableVersions,
} from '~/features/deliverables/features/packages/actions/packageDeliverableActions';

interface Props {
    serialNumber: string;
}

const defaultFilters = {
    deliverableId: undefined,
    targetDeliverableVersion: undefined,
    result: ALL,
    from: undefined,
    to: undefined,
    showOnlyLastEvent: undefined,
};

const FETCH_UPDATE_EVENTS_AMOUNT_INCREMENT = 50;

const DeviceUpdateEventsTab: React.FC<Props> = ({ serialNumber }) => {
    const dispatch = useDispatch();
    const packages = useSelector(packageEntitiesSelector);
    const packageDeliverableVersions = useSelector(filterDeliverableVersionsSelector);
    const updateEvents = useSelector((state: any) => deviceUpdateEventsSelector(state, { serialNumber }));

    const [filters, setFilters] = useState(defaultFilters);
    const [page, setPage] = useState(1);

    const updateFilter = useCallback((newFilters: Partial<typeof defaultFilters>) => {
        const updatedFilters = { ...filters, ...newFilters };
        setFilters(updatedFilters);
        setPage(1);
    }, [filters]);

    const handleFilteredUpdateEvents = useCallback(() => {
        let searchCriteria = defaults(queryToSearchCriteria(filters), {
            hwSerial: serialNumber,
        });
        searchCriteria.outcome = searchCriteria.outcome !== ALL ? searchCriteria.outcome : undefined;
        const fetchFilteredUpdateEventsOptions = {
            page: 0,
            size: FETCH_UPDATE_EVENTS_AMOUNT_INCREMENT * page,
            searchCriteria,
        };
        dispatch(fetchFilteredUpdateEvents(fetchFilteredUpdateEventsOptions));
    }, [filters, page, serialNumber, dispatch]);

    useEffect(() => {
        dispatch(fetchPackages());
        updateFilter(defaultFilters);
    }, [dispatch]);

    useEffect(() => {
        handleFilteredUpdateEvents();
    }, [handleFilteredUpdateEvents]);

    const onDeliverableIdChange = ({ deliverableId }: { deliverableId: string }) => {
        updateFilter({
            deliverableId,
            targetDeliverableVersion: undefined,
        });
        if (deliverableId !== undefined) {
            dispatch(fetchPackageDeliverableVersions(deliverableId));
        }
    };

    const onTargetDeliverableVersionChange = ({ suggestionId }: { suggestionId: string }) => {
        updateFilter({
            targetDeliverableVersion: suggestionId || undefined,
        });
    };

    const onShowOnlyLastEventChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateFilter({
            showOnlyLastEvent: event.currentTarget.checked,
        });
    };

    const onResultChange = ({ result }: { result: string }) => {
        updateFilter({ result });
    };

    const onFromToChange = (startValue: any, endValue: any) => {
        updateFilter({
            from: startValue ? startValue.valueOf() : undefined,
            to: endValue ? endValue.valueOf() : undefined,
        });
    };

    const clearFilters = () => {
        updateFilter({
            ...defaultFilters,
            result: undefined,
        });
    };

    const deliverableOptions = createDeliverablesOptions(packages);
    const packageVersionsOptions = createDeliverablesVersionOptions(packageDeliverableVersions);

    return (
        <div style={{ backgroundColor: '#F5F5F5' }} className={'padding-bottom-10'}>
            <div className={'display-flex flex-column'}>
                <ExpanderPanel className={'rounded-large bg-white margin-top-10 margin-left-10 margin-right-10 margin-bottom-0'} title={
                    <div className={"display-flex align-items-center text-size-18"}>
                        <span className={"rioglyph rioglyph-filter"}></span>
                        <span className={"margin-left-5 h5 margin-top-0 margin-bottom-0"}><FormattedMessage id={'intl-msg:filters'}/></span>
                    </div>
                }>
                    <DeviceUpdateEventsFilters
                        filters={filters}
                        deliverableOptions={deliverableOptions}
                        packageVersionsOptions={packageVersionsOptions}
                        onDeliverableIdChange={onDeliverableIdChange}
                        onTargetDeliverableVersionChange={onTargetDeliverableVersionChange}
                        onResultChange={onResultChange}
                        onFromToChange={onFromToChange}
                        onShowOnlyLastEventChange={onShowOnlyLastEventChange}
                        clearFilters={clearFilters}
                    />
                </ExpanderPanel>
                <div className={'rounded-large bg-white margin-top-10 margin-left-10 margin-right-10 padding-20'}>
                    <DeviceUpdateEventsContainer
                        updateEvents={updateEvents}
                        autoReload={false}
                        showSerialNumber={false}
                        page={page}
                        setUpdateEventsPage={setPage}
                        serialNumber={serialNumber}
                    />
                </div>
            </div>
        </div>
    );
};

export default DeviceUpdateEventsTab;
